import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        code: '',
        amount: 0,
        is_percentage: false,
        usage_limit: 0,
        min_amount: 0,
        valid_from: '',
        valid_until: '',
        is_active: true,
        products: [],
        user: null,
        applicable_to_rentals: true,
        applicable_to_sales: true,
        applicable_to_formulas: true,
        applicable_to_workshops: true
    });

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const response = await getData('/coupons?populate=*&sort[0]=code:asc');
            // console.log(response);
            setCoupons(response.data.data);
            setError(null);
        } catch (err) {
            setError('Error loading coupons');
            console.error('Error fetching coupons:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await getData('/products?sort[0]=titre:asc');
            //   console.log(response)
            setProducts(response.data.data);
        } catch (err) {
            console.error('Error fetching products:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await getData('/users?sort[0]=username:asc');
            setUsers(response.data);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    useEffect(() => {
        fetchCoupons();
        fetchProducts();
        fetchUsers();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            }
            return [...prev, productId];
        });
    };

    const handleSelectAllProducts = (e) => {
        if (e.target.checked) {
            // Select all products
            setSelectedProducts(products.map(product => product.id));
        } else {
            // Deselect all products
            setSelectedProducts([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                data: {
                    ...formData,
                    valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
                    valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
                    products: selectedProducts,
                    user: formData.user || null
                }
            };

            if (editingCoupon) {
                await putData(`/coupons/${editingCoupon.documentId}`, submitData);
                alert('Coupon updated successfully');
            } else {
                await postData('/coupons', submitData);
                alert('Coupon created successfully');
            }

            const modal = document.getElementById('couponModal');
            const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
            bootstrapModal.hide();

            setFormData({
                code: '',
                amount: 0,
                is_percentage: false,
                usage_limit: 0,
                min_amount: 0,
                valid_from: '',
                valid_until: '',
                is_active: true,
                products: [],
                user: null,
                applicable_to_rentals: true,
                applicable_to_sales: true,
                applicable_to_formulas: true,
                applicable_to_workshops: true
            });
            setSelectedProducts([]);
            setEditingCoupon(null);
            await fetchCoupons();
        } catch (err) {
            alert('Error saving coupon');
            console.error('Error saving coupon:', err);
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Convert UTC to local time and format
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
            .toISOString()
            .slice(0, 16);
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Guadeloupe'
        }).format(date);
    };

    const handleEdit = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            amount: coupon.amount,
            is_percentage: coupon.is_percentage,
            usage_limit: coupon.usage_limit,
            min_amount: coupon.min_amount,
            valid_from: formatDateForInput(coupon.valid_from),
            valid_until: formatDateForInput(coupon.valid_until),
            is_active: coupon.is_active,
            user: coupon.user?.id || null,
            applicable_to_rentals: coupon.applicable_to_rentals ?? true,
            applicable_to_sales: coupon.applicable_to_sales ?? true,
            applicable_to_formulas: coupon.applicable_to_formulas ?? true,
            applicable_to_workshops: coupon.applicable_to_workshops ?? true
        });
        setSelectedProducts(coupon.products?.map(p => p.id) || []);
        const modal = new window.bootstrap.Modal(document.getElementById('couponModal'));
        modal.show();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this coupon?')) {
            try {
                await deleteData(`/coupons/${id}`);
                await fetchCoupons();
                alert('Coupon deleted successfully');
            } catch (err) {
                alert('Error deleting coupon');
                console.error('Error deleting coupon:', err);
            }
        }
    };

    const handleAddNew = () => {
        setEditingCoupon(null);
        setFormData({
            code: '',
            amount: 0,
            is_percentage: false,
            usage_limit: 0,
            min_amount: 0,
            valid_from: '',
            valid_until: '',
            is_active: true,
            products: [],
            user: null,
            applicable_to_rentals: true,
            applicable_to_sales: true,
            applicable_to_formulas: true,
            applicable_to_workshops: true
        });
        setSelectedProducts([]);
        const modal = new window.bootstrap.Modal(document.getElementById('couponModal'));
        modal.show();
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="h4 mb-0">Coupons</h2>
                <div>
                    <button
                        onClick={fetchCoupons}
                        className="btn btn-outline-primary me-2"
                    >
                        Actualiser
                    </button>
                    <button
                        onClick={handleAddNew}
                        className="btn btn-primary"
                    >
                        Nouveau Coupon
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-body">
                    {coupons.length === 0 ? (
                        <p className="text-center text-muted my-5">Aucun coupon disponible</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Code</th>
                                        <th>Montant</th>
                                        <th>Type</th>
                                        <th>Applicable à</th>
                                        <th>Valable jusqu'au</th>
                                        <th>Statut</th>
                                        <th>Produits</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {coupons.map((coupon) => {
                                        const applicableTypes = [];
                                        if (coupon.applicable_to_rentals ?? true) applicableTypes.push('Locations');
                                        if (coupon.applicable_to_sales ?? true) applicableTypes.push('Ventes');
                                        if (coupon.applicable_to_formulas ?? true) applicableTypes.push('Formules');
                                        // if (coupon.applicable_to_workshops ?? true) applicableTypes.push('Ateliers');

                                        return (
                                            <tr key={coupon.id}>
                                                <td>{coupon.code}</td>
                                                <td>{coupon.amount}{coupon.is_percentage ? '%' : '€'}</td>
                                                <td>{coupon.is_percentage ? 'Pourcentage' : 'Fixe'}</td>
                                                <td>
                                                    <small>{applicableTypes.length > 0 ? applicableTypes.join(', ') : 'Aucun'}</small>
                                                </td>
                                                <td>{formatDateForDisplay(coupon.valid_until)}</td>
                                                <td>
                                                    <span className={`badge ${coupon.is_active ? 'bg-success' : 'bg-danger'}`}>
                                                        {coupon.is_active ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </td>
                                                <td>{coupon.products?.length || 0} produits</td>
                                                <td>
                                                    <div className="btn-group">
                                                        <button
                                                            className="btn btn-sm btn-primary me-2"
                                                            onClick={() => handleEdit(coupon)}
                                                        >
                                                            Modifier
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(coupon.documentId)}
                                                        >
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal pour Ajouter/Modifier un Coupon */}
            <div className="modal fade" id="couponModal" tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <form onSubmit={handleSubmit}>
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingCoupon ? 'Modifier le Coupon' : 'Nouveau Coupon'}
                                </h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label htmlFor="code" className="form-label">Code</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="code"
                                                name="code"
                                                value={formData.code}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="amount" className="form-label">Montant</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="amount"
                                                name="amount"
                                                value={formData.amount}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="mb-3 form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_percentage"
                                                name="is_percentage"
                                                checked={formData.is_percentage}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="is_percentage">
                                                En pourcentage
                                            </label>
                                        </div>

                                        {/* <div className="mb-3">
                                            <label htmlFor="usage_limit" className="form-label">Limite d'utilisation</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="usage_limit"
                                                name="usage_limit"
                                                value={formData.usage_limit}
                                                onChange={handleInputChange}
                                            />
                                        </div> */}

                                        <div className="mb-3">
                                            <label htmlFor="min_amount" className="form-label">Montant minimum d'achat</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="min_amount"
                                                name="min_amount"
                                                value={formData.min_amount}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        <div className='mb-3'>
                                            <label htmlFor="user" className="form-label">Limiter le code à un utilisateur (optionel)</label><br />
                                            <small>Pour limiter, sélectionnez l'utilisateur.</small>
                                            <select
                                                className="form-select"
                                                id="user"
                                                name="user"
                                                value={formData.user || ''}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Tous les utilisateurs</option>
                                                {users.map(user => (
                                                    <option key={user.id} value={user.id}>
                                                        {user.username || user.email}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Types de vente applicables</label>
                                            <div className="border p-3 rounded">
                                                <div className="form-check mb-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="applicable_to_rentals"
                                                        name="applicable_to_rentals"
                                                        checked={formData.applicable_to_rentals}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="applicable_to_rentals">
                                                        Locations
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="applicable_to_sales"
                                                        name="applicable_to_sales"
                                                        checked={formData.applicable_to_sales}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="applicable_to_sales">
                                                        Produits à la vente
                                                    </label>
                                                </div>
                                                <div className="form-check mb-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="applicable_to_formulas"
                                                        name="applicable_to_formulas"
                                                        checked={formData.applicable_to_formulas}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="applicable_to_formulas">
                                                        Formules
                                                    </label>
                                                </div>
                                                {/* <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="applicable_to_workshops"
                                                        name="applicable_to_workshops"
                                                        checked={formData.applicable_to_workshops}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="applicable_to_workshops">
                                                        Ateliers
                                                    </label>
                                                </div> */}
                                            </div>
                                            <small className="text-muted">
                                                Sélectionnez les types de vente sur lesquels ce coupon peut être appliqué
                                            </small>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label htmlFor="valid_from" className="form-label">Valable à partir du</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                id="valid_from"
                                                name="valid_from"
                                                value={formData.valid_from}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="valid_until" className="form-label">Valable jusqu'au</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                id="valid_until"
                                                name="valid_until"
                                                value={formData.valid_until}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        <div className="mb-3 form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="is_active"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label" htmlFor="is_active">
                                                Actif
                                            </label>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label">Sélectionner les produits</label>
                                            <div className="border p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                <div className="form-check mb-2 border-bottom pb-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id="select-all-products"
                                                        checked={selectedProducts.length === products.length}
                                                        onChange={handleSelectAllProducts}
                                                    />
                                                    <label className="form-check-label" htmlFor="select-all-products">
                                                        <strong>Sélectionner tout</strong>
                                                    </label>
                                                </div>
                                                {products.map(product => (
                                                    <div key={product.id} className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            id={`product-${product.id}`}
                                                            checked={selectedProducts.includes(product.id)}
                                                            onChange={() => handleProductSelect(product.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`product-${product.id}`}>
                                                            {product.titre}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                            <small className="text-muted">
                                                {selectedProducts.length} produits sélectionnés
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                                    Annuler
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingCoupon ? 'Mettre à jour' : 'Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Coupons;